import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/auth";
import { useI18n } from "@/i18n";
import { Plus, X, Trash2, ClipboardList, Printer, Package as PackageIcon, ScanLine, CheckCircle2, AlertTriangle } from "lucide-react";
import ScannerInput from "@/components/ScannerInput";
import { toast } from "sonner";
import jsPDF from "jspdf";
import JsBarcode from "jsbarcode";
import { ensureArabicFont, pdfText, hasArabic } from "@/lib/pdfFonts";
import { pdfStr, isRTL } from "@/lib/pdfStrings";
import { PdfLangMenu } from "@/components/PdfLangMenu";

const EMPTY = {
  nombre: "", modo: "terrestre", tipo: "sucursal_out",
  vehiculo: "", conductor: "",
  origen: "", destino: "", oficina_destino: "",
  warehouse_id: "", voyage_id: "", container_number: "",
  fecha_salida: "", notas: "", shipment_ids: [],
};

const TIPO_INFO = {
  sucursal_out: { color: "bg-blue-100 text-blue-700", labelKey: "manifest_sucursal_out" },
  hub_in: { color: "bg-purple-100 text-purple-700", labelKey: "manifest_hub_in" },
  hub_out: { color: "bg-emerald-100 text-emerald-700", labelKey: "manifest_hub_out" },
  recogida: { color: "bg-amber-100 text-amber-700", labelKey: "pickups_tab" },
};

// Manifest PDF strings by language
const PDF_STRINGS = {
  es: { title: "Manifiesto de carga", mode: "Modo", vehicle: "Vehículo", driver: "Conductor", origin: "Origen", destination: "Destino", departure: "Salida", warehouse: "Almacén", container: "Contenedor", vessel: "Vessel", voyage: "Viaje", shipments: "ENVÍOS", tracking: "Tracking", route: "Ruta", status: "Estado", weight: "Peso", cbm: "CBM", total: "Total", envios_count: "envíos", notes: "Notas", missing: "Faltantes", official: "Manifiesto oficial", scan_barcode: "Escanea este código para recibir el manifiesto" },
  en: { title: "Cargo Manifest", mode: "Mode", vehicle: "Vehicle", driver: "Driver", origin: "Origin", destination: "Destination", departure: "Departure", warehouse: "Warehouse", container: "Container", vessel: "Vessel", voyage: "Voyage", shipments: "SHIPMENTS", tracking: "Tracking", route: "Route", status: "Status", weight: "Weight", cbm: "CBM", total: "Total", envios_count: "shipments", notes: "Notes", missing: "Missing", official: "Official manifest", scan_barcode: "Scan this code to receive the manifest" },
  ar: { title: "بيان الشحن", mode: "الوسيلة", vehicle: "المركبة", driver: "السائق", origin: "المصدر", destination: "الوجهة", departure: "المغادرة", warehouse: "المستودع", container: "الحاوية", vessel: "السفينة", voyage: "الرحلة", shipments: "الشحنات", tracking: "التتبع", route: "المسار", status: "الحالة", weight: "الوزن", cbm: "متر مكعب", total: "المجموع", envios_count: "شحنات", notes: "ملاحظات", missing: "المفقودة", official: "بيان رسمي", scan_barcode: "امسح هذا الرمز لاستلام البيان" },
};

export default function Manifests() {
  const { api, user } = useAuth();
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState("all");
  const [shipments, setShipments] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [voyages, setVoyages] = useState([]);
  const activeVoyages = useMemo(() => voyages.filter(v => v.activa), [voyages]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [expanded, setExpanded] = useState(null);
  const [scanBar, setScanBar] = useState("");
  const [pdfLang, setPdfLang] = useState("en"); // English default
  const [receiveModal, setReceiveModal] = useState(null); // manifest doc
  const [missingInputs, setMissingInputs] = useState([]);

  const canReceive = ["admin", "operaciones", "almacen"].includes(user.role);

  const load = () => api.get("/manifests").then(r => setRows(r.data)).catch(()=>{});
  useEffect(() => {
    load();
    api.get("/shipments").then(r => setShipments(r.data)).catch(()=>{});
    api.get("/warehouses").then(r => setWarehouses(r.data)).catch(()=>{});
    api.get("/voyages").then(r => setVoyages(r.data)).catch(()=>{});
  // eslint-disable-next-line
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/manifests", form);
      toast.success(t("saved"));
      setForm(EMPTY); setShowForm(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const remove = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    await api.delete(`/manifests/${id}`);
    load();
  };

  const toggleShipment = (id) => {
    setForm(f => ({
      ...f,
      shipment_ids: f.shipment_ids.includes(id)
        ? f.shipment_ids.filter(x => x !== id)
        : [...f.shipment_ids, id],
    }));
  };

  const scanManifest = async (codeArg) => {
    const c = (codeArg ?? scanBar).trim();
    if (!c) return;
    try {
      const { data } = await api.get(`/manifests/scan/${encodeURIComponent(c)}`);
      setReceiveModal(data);
      setMissingInputs([]);
      setScanBar("");
    } catch (err) { toast.error(err.response?.data?.detail || t("not_found")); }
  };

  const submitReceive = async () => {
    try {
      const missing = missingInputs.filter(m => m.tracking_code.trim());
      await api.post(`/manifests/${receiveModal.id}/receive`, { missing });
      toast.success(`✓ ${(receiveModal.shipment_ids?.length || 0) - missing.length} recibidos, ${missing.length} faltantes`);
      setReceiveModal(null);
      setMissingInputs([]);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const printManifest = async (m, langOverride = null) => {
    const activeLang = langOverride || pdfLang;
    const S = PDF_STRINGS[activeLang] || PDF_STRINGS.en;
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const w = doc.internal.pageSize.getWidth();

    // Load Arabic font if AR selected OR any dynamic field contains Arabic chars
    const anyArabic = activeLang === "ar" || [m.nombre, m.origen, m.destino, m.vehiculo, m.conductor, m.notas]
      .concat((m.shipments || []).map(s => `${s.tracking_code} ${s.origen} ${s.destino}`))
      .some(hasArabic);
    let arabicReady = false;
    if (anyArabic) {
      arabicReady = await ensureArabicFont(doc);
      if (!arabicReady && activeLang === "ar") {
        toast.error("No se pudo cargar la fuente árabe, usando inglés");
      }
    }
    const setBodyFont = (weight = "normal") => {
      if (activeLang === "ar" && arabicReady) {
        doc.setFont("Amiri", "normal");
      } else {
        doc.setFont("helvetica", weight);
      }
    };

    let barcodeDataUrl = null;
    const barcodeValue = (m.manifest_barcode || m.codigo || "").trim();
    if (barcodeValue) {
      try {
        const canvas = document.createElement("canvas");
        JsBarcode(canvas, barcodeValue, { format: "CODE128", displayValue: false, height: 45, margin: 0, width: 1.6 });
        barcodeDataUrl = canvas.toDataURL("image/png");
      } catch (e) { console.warn("Manifest barcode failed:", e); }
    }

    const img = new Image();
    img.onload = () => { try { doc.addImage(img, "PNG", 30, 14, 58, 26); } catch (e) { console.warn("Manifest logo failed:", e); } render(); };
    img.onerror = render;
    img.src = "/logo.png";
    function render() {
      // ======= HEADER: black band with brand + title =======
      doc.setFillColor(15, 23, 42);
      doc.rect(0, 0, w, 54, "F");
      doc.setFont("helvetica","bold"); doc.setFontSize(20); doc.setTextColor(255, 255, 255);
      doc.text("MEOS CARGO", w - 40, 30, { align: "right" });
      doc.setFont("helvetica","normal"); doc.setFontSize(9); setBodyFont("normal"); doc.setTextColor(203, 213, 225);
      pdfText(doc, S.title, w - 40, 46, { align: "right" });
      doc.setFont("helvetica","normal");

      // ======= RED SERVICE BAND: mode + date =======
      const modeColor = m.modo === "aereo" ? [30, 64, 175] : m.modo === "maritimo" ? [8, 145, 178] : [185, 28, 28];
      doc.setFillColor(...modeColor);
      doc.rect(0, 54, w, 24, "F");
      doc.setFont("helvetica","bold"); doc.setFontSize(11); doc.setTextColor(255, 255, 255);
      doc.text((m.modo || "").toUpperCase(), 40, 71);
      doc.setFontSize(10);
      doc.text(new Date().toLocaleString(), w - 40, 71, { align: "right" });

      // ======= MANIFEST CODE + BARCODE ROW =======
      let y = 100;
      doc.setFont("helvetica","bold"); doc.setFontSize(16); doc.setTextColor(15,23,42);
      pdfText(doc, m.codigo, 40, y);
      y += 18;
      doc.setFont("helvetica","normal"); doc.setFontSize(11); doc.setTextColor(71, 85, 105);
      pdfText(doc, m.nombre, 40, y);

      // Barcode box (right side)
      if (barcodeDataUrl) {
        try {
          doc.addImage(barcodeDataUrl, "PNG", w - 220, 90, 180, 45);
          doc.setFont("courier","bold"); doc.setFontSize(8); doc.setTextColor(0, 0, 0);
          doc.text(m.manifest_barcode || m.codigo, w - 130, 145, { align: "center" });
        } catch (e) { console.warn("Manifest barcode embed failed:", e); }
      }

      // ======= META GRID: origin/dest/vehicle/driver =======
      y += 30;
      doc.setDrawColor(226, 232, 240); doc.setLineWidth(0.5);
      doc.setFillColor(248, 250, 252);
      doc.rect(40, y, w - 80, 60, "F");
      doc.setDrawColor(226,232,240); doc.rect(40, y, w - 80, 60);
      doc.setFont("helvetica","bold"); doc.setFontSize(7); doc.setTextColor(100, 116, 139);
      const col = (w - 80) / 4;
      doc.text(S.origin.toUpperCase(), 50, y + 14);
      doc.text(S.destination.toUpperCase(), 50 + col, y + 14);
      doc.text(S.vehicle.toUpperCase(), 50 + col * 2, y + 14);
      doc.text(S.driver.toUpperCase(), 50 + col * 3, y + 14);
      doc.setFont("helvetica","bold"); doc.setFontSize(11); doc.setTextColor(15, 23, 42);
      pdfText(doc, m.origen || "-", 50, y + 32);
      pdfText(doc, m.destino || "-", 50 + col, y + 32);
      pdfText(doc, m.vehiculo || "-", 50 + col * 2, y + 32);
      pdfText(doc, m.conductor || "-", 50 + col * 3, y + 32);
      doc.setFont("helvetica","normal"); doc.setFontSize(8); doc.setTextColor(100, 116, 139);
      pdfText(doc, m.fecha_salida ? `${S.departure}: ${m.fecha_salida}` : "", 50, y + 50);
      y += 70;

      // ======= VOYAGE / CONTAINER (if maritime) =======
      if (m.container_number || m.voyage_id) {
        const voy = voyages.find(v => v.id === m.voyage_id) || m.voyage_ref || null;
        doc.setFillColor(30, 41, 59);
        doc.rect(40, y, w - 80, 26, "F");
        doc.setFont("helvetica","bold"); doc.setFontSize(8); doc.setTextColor(203, 213, 225);
        doc.text(`${S.voyage.toUpperCase()}`, 50, y + 11);
        doc.text(`${S.vessel.toUpperCase()}`, 50 + col * 2, y + 11);
        doc.setFontSize(10); doc.setTextColor(255, 255, 255);
        pdfText(doc, voy?.codigo || "-", 50, y + 22);
        pdfText(doc, voy?.vessel_number || m.container_number || "-", 50 + col * 2, y + 22);
        y += 34;
      }

      // ======= SHIPMENTS TABLE =======
      y += 12;
      setBodyFont("bold"); doc.setFontSize(10); doc.setTextColor(15, 23, 42);
      pdfText(doc, S.shipments, 40, y);
      y += 6;
      doc.setFillColor(15, 23, 42);
      doc.rect(40, y, w - 80, 18, "F");
      doc.setFont("helvetica","bold"); doc.setFontSize(8); doc.setTextColor(255, 255, 255);
      doc.text("#", 46, y + 12);
      doc.text(S.tracking.toUpperCase(), 68, y + 12);
      doc.text(S.route.toUpperCase(), 210, y + 12);
      doc.text(S.weight.toUpperCase(), w - 158, y + 12);
      doc.text(S.status.toUpperCase(), w - 92, y + 12);
      y += 20;
      doc.setFont("helvetica","normal"); doc.setFontSize(9); doc.setTextColor(30, 41, 59);
      (m.shipments || []).forEach((s, i) => {
        // zebra stripe
        if (i % 2 === 0) { doc.setFillColor(248, 250, 252); doc.rect(40, y - 12, w - 80, 16, "F"); }
        if (y > 780) { doc.addPage(); y = 60; }
        doc.text(String(i + 1), 46, y);
        doc.setFont("courier","normal"); doc.setFontSize(9);
        pdfText(doc, s.tracking_code || "-", 68, y);
        doc.setFont("helvetica","normal");
        pdfText(doc, `${s.origen || ""} > ${s.destino || ""}`.substring(0, 30), 210, y);
        doc.setFont("courier","normal");
        doc.text(`${s.peso || 0} kg`, w - 158, y);
        doc.setFont("helvetica","bold"); doc.setFontSize(7); doc.setTextColor(5, 150, 105);
        doc.text((s.estado || "").toUpperCase(), w - 92, y);
        doc.setFont("helvetica","normal"); doc.setFontSize(9); doc.setTextColor(30, 41, 59);
        y += 16;
      });

      // ======= TOTAL BAR =======
      y += 6;
      doc.setDrawColor(15, 23, 42); doc.setLineWidth(1);
      doc.line(40, y, w - 40, y);
      y += 16;
      setBodyFont("bold"); doc.setFontSize(11); doc.setTextColor(15, 23, 42);
      const totalPeso = (m.shipments || []).reduce((s, x) => s + (x.peso || 0), 0);
      pdfText(doc, `${S.total}: ${(m.shipments || []).length} ${S.envios_count}   ·   ${totalPeso.toFixed(2)} kg`, 40, y);

      // ======= INCIDENTS =======
      if ((m.missing_items || []).length > 0) {
        y += 24;
        doc.setFillColor(254, 226, 226);
        doc.rect(40, y - 12, w - 80, 20 + (m.missing_items.length * 12), "F");
        doc.setFontSize(9); doc.setTextColor(185, 28, 28); setBodyFont("bold");
        pdfText(doc, `${S.missing}:`, 46, y);
        doc.setFont("helvetica","normal"); doc.setFontSize(8);
        m.missing_items.forEach(mi => {
          y += 12;
          pdfText(doc, `• ${mi.tracking_code}${mi.nota ? " · " + mi.nota : ""}`, 46, y);
        });
      }

      // ======= NOTES =======
      if (m.notas) {
        y += 20; doc.setFont("helvetica","normal"); doc.setFontSize(8); doc.setTextColor(80, 80, 80);
        const nlines = doc.splitTextToSize(`${S.notes}: ${m.notas}`, w - 80);
        nlines.slice(0, 3).forEach(line => { pdfText(doc, line, 40, y); y += 12; });
      }

      // ======= FOOTER =======
      doc.setDrawColor(226, 232, 240); doc.setLineWidth(0.5);
      doc.line(40, 810, w - 40, 810);
      doc.setFont("helvetica","normal"); doc.setFontSize(7); doc.setTextColor(148, 163, 184);
      pdfText(doc, `meoscargo.com · ${S.official}`, w/2, 822, { align: "center" });
      doc.save(`${m.codigo}.pdf`);
      toast.success("PDF");
    }
  };

  const inp = "w-full px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-red-600";
  const availableShipments = shipments.filter(s => s.estado !== "entregado");

  const filteredManifests = rows.filter(m => tab === "all" ? true : tab === "recogida" ? m.tipo === "recogida" : m.tipo !== "recogida");
  const todayStr = new Date().toISOString().slice(0, 10);
  const pickupsToday = rows.filter(m => m.tipo === "recogida" && (m.created_at || "").slice(0, 10) === todayStr);
  const pickupsTodayWeight = pickupsToday.reduce((sum, m) => sum + (m.total_peso || 0), 0);

  return (
    <div className="space-y-4" data-testid="manifests-page">
      {true && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-2.5 text-xs text-amber-900" data-testid="legacy-notice">
          {t("legacy_notice")}
        </div>
      )}
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold flex items-center gap-2"><ClipboardList className="w-5 h-5 text-red-600" /> {t("manifests")}</h2>
          <p className="text-sm text-slate-500">{filteredManifests.length} {t("manifests").toLowerCase()}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="text-xs font-semibold text-slate-500 flex items-center gap-2 border border-slate-300 rounded-lg px-2 py-2">
            PDF:
            <select data-testid="mf-lang" value={pdfLang} onChange={(e) => setPdfLang(e.target.value)} className="outline-none bg-transparent">
              <option value="en">English</option>
              <option value="es">Español</option>
              <option value="ar">العربية</option>
            </select>
          </label>
          <button data-testid="new-manifest-btn" onClick={() => { setForm(EMPTY); setShowForm(true); }} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-semibold text-sm flex items-center gap-2">
            <Plus className="w-4 h-4" /> {t("new_manifest")}
          </button>
        </div>
      </div>

      {/* Tabs: all / exits / pickups */}
      <div className="flex items-center gap-2 border-b border-slate-200">
        {[["all", t("all")], ["salida", t("manifests")], ["recogida", t("pickups_tab")]].map(([k, l]) => (
          <button key={k} data-testid={`man-tab-${k}`} onClick={() => setTab(k)}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === k ? "border-red-600 text-red-600" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            {l}{k === "recogida" && pickupsToday.length > 0 ? ` (${pickupsToday.length})` : ""}
          </button>
        ))}
      </div>

      {tab === "recogida" && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center justify-between" data-testid="pickups-summary">
          <div>
            <div className="text-xs font-semibold uppercase text-emerald-700">{t("pickups_today")}</div>
            <div className="text-2xl font-display font-bold text-emerald-800 tabular-nums">{pickupsToday.length}</div>
          </div>
          <div className="text-end">
            <div className="text-xs font-semibold uppercase text-emerald-700">{t("total_weight")}</div>
            <div className="text-2xl font-display font-bold text-emerald-800 tabular-nums">{pickupsTodayWeight.toFixed(1)} kg</div>
          </div>
        </div>
      )}

      {/* Scanner to receive an incoming manifest (warehouse/hub) */}
      {canReceive && (
        <div className="bg-slate-900 text-white rounded-xl p-4 space-y-2" data-testid="scan-manifest-panel">
          <div className="flex items-center gap-2">
            <ScanLine className="w-5 h-5 text-emerald-400" />
            <span className="font-semibold text-sm">{t("scan_manifest_barcode")}</span>
          </div>
          <ScannerInput
            dark
            testIdPrefix="scan-manifest"
            placeholder={t("scan_hint")}
            buttonLabel={t("receive")}
            onScan={(code) => scanManifest(code)}
          />
        </div>
      )}

      {filteredManifests.map(m => {
        const tipoInfo = TIPO_INFO[m.tipo || "sucursal_out"];
        return (
          <div key={m.id} data-testid={`man-${m.id}`} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="p-4 flex items-center justify-between flex-wrap gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono font-semibold text-slate-900">{m.codigo}</span>
                  <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${tipoInfo?.color || "bg-slate-100 text-slate-600"}`}>{t(tipoInfo?.labelKey) || m.tipo}</span>
                  <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">{(m.shipments||[]).length} {t("shipments").toLowerCase()}</span>
                  {m.estado === "recibido" && <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> {t("received")}</span>}
                  {(m.missing_items || []).length > 0 && <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-100 text-red-700 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {m.missing_items.length} {t("missing")}</span>}
                </div>
                <div className="text-sm font-semibold text-slate-800 mt-1">{m.nombre}</div>
                <div className="text-xs text-slate-500 truncate">{m.origen} → {m.destino} · {m.vehiculo || "—"} · {m.conductor || "—"} · <span className="font-mono">{m.manifest_barcode}</span></div>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <PdfLangMenu
                  testid={`man-print-${m.id}`}
                  compact
                  icon={<Printer className="w-4 h-4" />}
                  onPrint={(lg) => printManifest(m, lg)}
                />
                {canReceive && m.estado !== "recibido" && (
                  <button onClick={() => { setReceiveModal(m); setMissingInputs([]); }} className="text-xs font-semibold px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg" data-testid={`man-recv-${m.id}`}>{t("receive")}</button>
                )}
                <button onClick={() => setExpanded(expanded === m.id ? null : m.id)} className="text-xs font-semibold text-red-600 hover:underline px-2">{expanded === m.id ? "▲" : "▼"}</button>
                {user.role === "admin" && (
                  <button onClick={() => remove(m.id)} className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                )}
              </div>
            </div>
            {expanded === m.id && (
              <div className="p-4 border-t border-slate-100 bg-slate-50 space-y-1">
                {(m.shipments || []).map(s => (
                  <div key={s.id} className="flex items-center justify-between p-2 bg-white rounded-md text-xs">
                    <div className="flex items-center gap-2">
                      <PackageIcon className="w-3 h-3 text-slate-400" />
                      <span className="font-mono font-semibold">{s.tracking_code}</span>
                      <span className="text-slate-500">{s.origen} → {s.destino}</span>
                    </div>
                    <span className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{s.estado}</span>
                  </div>
                ))}
                {(m.missing_items || []).length > 0 && (
                  <div className="mt-3 pt-3 border-t border-red-200">
                    <div className="text-xs font-bold text-red-700 mb-1">⚠ {t("missing")}:</div>
                    {m.missing_items.map((mi, i) => (
                      <div key={mi.tracking_code || `mi-${i}`} className="flex justify-between p-2 bg-red-50 rounded text-xs font-mono border border-red-100"><span>{mi.tracking_code}</span><span className="text-slate-500">{mi.nota}</span></div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {filteredManifests.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-16 text-center text-slate-400">
          <ClipboardList className="w-14 h-14 mx-auto opacity-30 mb-3" />
          <p>{t("no_manifests")}</p>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <form onSubmit={submit} className="bg-white rounded-xl w-full max-w-3xl p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-semibold text-lg">{t("new_manifest")}</h3>
              <button type="button" onClick={()=>setShowForm(false)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            {(user.role === "admin" || user.role === "almacen") && (
              <div className="grid grid-cols-3 gap-2">
                {Object.entries(TIPO_INFO).map(([key, info]) => (
                  <button key={key} type="button" onClick={() => setForm({ ...form, tipo: key })} data-testid={`mf-tipo-${key}`} className={`py-2 rounded-lg border-2 font-semibold text-xs transition ${form.tipo === key ? "border-red-600 bg-red-50 text-red-700" : "border-slate-200 text-slate-500"}`}>{t(info.labelKey)}</button>
                ))}
              </div>
            )}
            <input required data-testid="mf-nombre" placeholder={t("name")} value={form.nombre} onChange={(e)=>setForm({...form, nombre: e.target.value})} className={inp} />
            <div className="grid grid-cols-2 gap-3">
              <select value={form.modo} onChange={(e)=>setForm({...form, modo: e.target.value})} className={inp}>
                <option value="terrestre">{t("ground") || "Terrestre"}</option>
                <option value="aereo">{t("air") || "Aéreo"}</option>
                <option value="maritimo">{t("sea") || "Marítimo"}</option>
              </select>
              <input type="date" value={form.fecha_salida} onChange={(e)=>setForm({...form, fecha_salida: e.target.value})} className={inp} />
              <input placeholder={t("vehicle") || "Vehículo"} value={form.vehiculo} onChange={(e)=>setForm({...form, vehiculo: e.target.value})} className={inp} />
              <input placeholder={t("driver") || "Conductor"} value={form.conductor} onChange={(e)=>setForm({...form, conductor: e.target.value})} className={inp} />
              <input placeholder={t("origin")} value={form.origen} onChange={(e)=>setForm({...form, origen: e.target.value})} className={inp} />
              <input placeholder={t("destination")} value={form.destino} onChange={(e)=>setForm({...form, destino: e.target.value})} className={inp} />
            </div>

            {(form.tipo === "hub_out") && (
              <div className="grid grid-cols-2 gap-3 border-2 border-emerald-200 bg-emerald-50/40 rounded-lg p-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">{t("container_number")}</label>
                  <input data-testid="mf-container" value={form.container_number} onChange={(e)=>setForm({...form, container_number: e.target.value})} placeholder="MSKU1234567" className={inp} />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">{t("voyage")}</label>
                  <select data-testid="mf-voyage" value={form.voyage_id} onChange={(e)=>setForm({...form, voyage_id: e.target.value})} className={inp}>
                    <option value="">—</option>
                    {activeVoyages.map(v => <option key={v.id} value={v.id}>{v.codigo} · {v.puerto_origen} → {v.puerto_destino}</option>)}
                  </select>
                </div>
              </div>
            )}

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-2">{t("assign_shipments")} ({form.shipment_ids.length})</label>
              <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
                {availableShipments.map(s => {
                  const on = form.shipment_ids.includes(s.id);
                  return (
                    <label key={s.id} data-testid={`mf-ship-${s.id}`} className={`flex items-center gap-3 p-2 cursor-pointer text-xs ${on?"bg-red-50":"hover:bg-slate-50"}`}>
                      <input type="checkbox" checked={on} onChange={()=>toggleShipment(s.id)} className="accent-red-600" />
                      <span className="font-mono font-semibold">{s.tracking_code}</span>
                      <span className="text-slate-500 flex-1 truncate">{s.origen} → {s.destino} · {s.cliente_nombre || ""}</span>
                      <span className="text-[9px] uppercase font-semibold px-1.5 py-0.5 rounded bg-slate-100">{s.estado}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <textarea rows="2" placeholder={t("notes")} value={form.notas} onChange={(e)=>setForm({...form, notas: e.target.value})} className={inp} />

            <div className="flex gap-3 pt-2">
              <button type="button" onClick={()=>setShowForm(false)} className="flex-1 border border-slate-300 py-2.5 rounded-lg font-semibold text-sm">{t("cancel")}</button>
              <button type="submit" data-testid="mf-submit" className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2.5 rounded-lg font-semibold text-sm">{t("save")}</button>
            </div>
          </form>
        </div>
      )}

      {receiveModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl p-6 space-y-3 max-h-[90vh] overflow-y-auto" data-testid="receive-modal">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-display font-semibold text-lg flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-600" /> {t("receive_manifest")}</h3>
                <p className="text-sm text-slate-500 font-mono">{receiveModal.codigo}</p>
              </div>
              <button onClick={() => setReceiveModal(null)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 text-sm">
              <div><b>{t("sender")}:</b> {receiveModal.oficina_origen || receiveModal.origen || "—"}</div>
              <div><b>{t("shipments")}:</b> {(receiveModal.shipment_ids || []).length}</div>
            </div>
            <div className="border border-red-200 rounded-lg p-3 bg-red-50/40">
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs font-semibold text-red-700 flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> {t("mark_missing")}</span>
                <button onClick={() => setMissingInputs([...missingInputs, { _id: `m-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, tracking_code: "", nota: "" }])} className="text-xs font-semibold text-red-600 hover:underline" data-testid="add-missing">+ {t("add")}</button>
              </div>
              {missingInputs.length === 0 && <p className="text-[11px] text-slate-500 italic">{t("all_received_hint")}</p>}
              {missingInputs.map((mi, i) => (
                <div key={mi._id || `mi-${i}`} className="flex gap-2 mb-2">
                  <select value={mi.tracking_code} onChange={(e) => setMissingInputs(missingInputs.map((x, j) => j === i ? { ...x, tracking_code: e.target.value } : x))} data-testid={`missing-tc-${i}`} className={inp + " flex-1"}>
                    <option value="">— {t("select")} —</option>
                    {(receiveModal.shipments || []).map(s => <option key={s.tracking_code} value={s.tracking_code}>{s.tracking_code}</option>)}
                  </select>
                  <input value={mi.nota} onChange={(e) => setMissingInputs(missingInputs.map((x, j) => j === i ? { ...x, nota: e.target.value } : x))} placeholder={t("note_optional")} className={inp + " flex-1"} />
                  <button onClick={() => setMissingInputs(missingInputs.filter((_, j) => j !== i))} className="p-2 text-slate-400 hover:text-red-600 rounded"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
            <div className="flex gap-3">
              <button onClick={() => setReceiveModal(null)} className="flex-1 border border-slate-300 py-2.5 rounded-lg font-semibold text-sm">{t("cancel")}</button>
              <button onClick={submitReceive} data-testid="confirm-receive" className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white py-2.5 rounded-lg font-semibold text-sm">{t("confirm_receipt")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
